import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Skeleton } from "primereact/skeleton";
import { requestGetCompliances } from "../../services/wisdomServices/complianceService";

function ManagerDashComplianceTable({ user, selectedProjectId = 0 }) {
  const [compliances, setCompliances] = useState([]);
  const [filteredCompliances, setFilteredCompliances] = useState([]);
  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [totalCompliances, setTotalCompliances] = useState(0);
  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      user: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      managerName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      projectName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    });

    setGlobalFilterValue("");
  };

  const loadCompliances = async () => {
    setLoading(true);
    try {
      const page = first / rows + 1;
      const response = await requestGetCompliances(
        true,
        selectedProjectId || 0,
        page,
        rows
      )

      setCompliances(response?.data || []);
      setFilteredCompliances(response?.data || []);
      setTotalCompliances(response?.pagination?.totalResults || 0);
    } catch (error) {
      console.error(error);
      setCompliances([]);
      setFilteredCompliances([]);
      setTotalCompliances(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initFilters();
  }, []);

  useEffect(() => {
    setFirst(0);
  }, [selectedProjectId]);

  useEffect(() => {
    if (user) {
      loadCompliances();
    }
  }, [user, selectedProjectId, first, rows]);

  const complianceBodyTemplate = (compliance) => {
    return (
      <Link
        className="manager-dashboard-requests"
        to={`/manager/compliance/${compliance.userId}`}
      >
        <strong>{compliance.name}</strong>
      </Link>
    );
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);

    if (!value) {
      setFilteredCompliances(compliances);
      return;
    }

    const lower = value.toLowerCase();

    const filtered = compliances.filter((item) =>
      item.name?.toLowerCase().includes(lower) ||
      item.managerName?.toLowerCase().includes(lower) ||
      item.projectName?.toLowerCase().includes(lower) ||
      item.complianceType?.toLowerCase().includes(lower)
    );

    setFilteredCompliances(filtered);
  };

  const header = (
    <div className="users-table-header">
      <h5 className="manager-dash-table-titles">
        Compliance Items: {totalCompliances}
      </h5>

      <div className="table-filters">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={() => {
            initFilters();
            setFilteredCompliances(compliances);
          }}
        />

        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            style={{ paddingLeft: "2.25rem" }}
          />
        </IconField>
      </div>
    </div>
  );

  if (loading) {
    return <Skeleton width="100%" height="22rem" borderRadius="12px" />;
  }

  return (
    <DataTable
      value={filteredCompliances}
      header={header}
      tableStyle={{ minWidth: "10rem" }}
      paginator
      lazy
      first={first}
      rows={rows}
      totalRecords={totalCompliances}
      onPage={(e) => {
        setFirst(e.first);
        setRows(e.rows);
      }}
      filters={filters}
      globalFilterFields={[
        "name",
        "managerName",
        "projectName",
        "complianceType",
      ]}
      emptyMessage="No compliance items found."
      onFilter={(e) => setFilters(e.filters)}
    >
      <Column header="Name" body={complianceBodyTemplate} />
      <Column header="Compliance Type" field="complianceType" />
      <Column header="Create Date" field="createDate" />
      <Column header="Manager Name" field="managerName" />
      <Column header="Project Name" field="projectName" />
    </DataTable>
  );
}

export default ManagerDashComplianceTable;